import { FunctionComponent, ReactNode } from "react";
import Icon from "@mdi/react";

export interface RecordCardField {
  label: string;
  value: ReactNode;
}

export interface RecordCardAction {
  icon: string;
  title: string;
  onClick: () => void;
  className?: string;
  /** Renders a filled text button (icon + label) instead of an icon-only one. */
  label?: string;
}

interface MobileRecordCardProps {
  /** Primary identifier — usually an id link or title node. */
  title: ReactNode;
  /** Status node, typically a <StatusBadge/>. Rendered top-right. */
  status?: ReactNode;
  /** A secondary status/badge row shown under the header. */
  subStatus?: ReactNode;
  fields: RecordCardField[];
  actions?: RecordCardAction[];
  onClick?: () => void;
  id?: string;
  leading?: ReactNode;
}

/**
 * The Orders-module mobile listing card: header (title + status), label/value
 * body rows, and an action-icon footer. Used inside <MobileRecordCardList/>.
 */
export const MobileRecordCard: FunctionComponent<MobileRecordCardProps> = ({
  title,
  status,
  subStatus,
  fields,
  actions,
  onClick,
  id,
  leading,
}) => {
  return (
    <div
      id={id}
      onClick={onClick}
      className={`rounded-lg border border-gray-300 shadow-sm bg-white p-3 text-sm ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {leading && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              {leading}
            </div>
          )}
          <div className="min-w-0 break-all font-semibold text-blue-500">
            {title}
          </div>
        </div>
        {status && <div className="shrink-0">{status}</div>}
      </div>

      {subStatus && <div className="mt-2">{subStatus}</div>}

      <div className="mt-2 flex flex-col gap-1 text-xs text-gray-600">
        {fields.map((f, i) => (
          <div key={i} className="flex justify-between gap-2">
            <span className="text-gray-400">{f.label}</span>
            <span className="font-medium text-gray-700 text-right break-words">
              {f.value ?? "-"}
            </span>
          </div>
        ))}
      </div>

      {actions && actions.length > 0 && (
        <div className="mt-2 flex items-center justify-end gap-3 border-t pt-2">
          {actions.map((a, i) => (
            <button
              id={id && `${id}-action-${i}`}
              key={i}
              title={a.title}
              onClick={(e) => {
                e.stopPropagation();
                a.onClick();
              }}
              className={`${
                a.label
                  ? "flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-myPrimaryColor hover:bg-mySecondaryColor text-white"
                  : "text-gray-500 hover:text-myPrimaryColor"
              } ${a.className ?? ""}`}
            >
              <Icon path={a.icon} className={a.label ? "h-4 w-4" : "h-5 w-5"} />
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface MobileRecordCardListProps {
  children: ReactNode;
}

/** Mobile-only stacked container for record cards (hidden on md+). */
export const MobileRecordCardList: FunctionComponent<
  MobileRecordCardListProps
> = ({ children }) => (
  <div className="md:hidden mt-3 flex flex-col gap-3">{children}</div>
);

export default MobileRecordCard;
